import {useEffect, useState} from 'react'
import {PageHeader, SaveCancelFooter} from '@/components/molecules'
import {Breadcrumb} from '@/components/atoms'
import {ConfirmationModal} from '@/components/molecules/ConfirmationModal'
import {useNavigate} from 'react-router-dom'
import {ROUTES} from '@/utils/constants/routesConstants'
import {CreateTierContainer} from '@/containers/ClubPlus/CreateTier/CreateTierContainer'
import {FormProvider, useForm} from 'react-hook-form'
import {createTierSchema, defaultValues} from '@/pages/ClubPlusPages/CreateTier/CreateTierSchemas'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {useIntl} from 'react-intl'
import {createTier} from '@/services/ClubPlus/TierService'
import './_createTier.scss'
import {zodResolver} from '@hookform/resolvers/zod'

export const CreateTier = () => {
  const {formatMessage} = useIntl()
  const methods = useForm({
    defaultValues,
    resolver: zodResolver(createTierSchema),
    mode: 'onChange',
  })

  const [cancelModal, setCancelModal] = useState(false)
  const [draftModal, setDraftModal] = useState(false)
  const [phase, setPhase] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [formIsValid, setFormIsValid] = useState({
    phase1: false,
    phase2: false,
  })
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  useEffect(() => {
    const subscription = methods.watch(() => {
      const activities = methods.getValues('activities') || []
      const hasValidActivities =
        activities.length > 0 &&
        activities.every(activity => activity.activityID && activity.activityName && activity.multiplier)

      const tierName = methods.getValues('name')

      setFormIsValid(prev => ({
        ...prev,
        phase1: tierName && hasValidActivities,
      }))
    })

    return () => subscription.unsubscribe()
  }, [methods])

  const getFormError = () => {
    if (phase === 1) {
      return !formIsValid.phase1
    } else if (phase === 2) {
      return !methods.formState.isValid
    }

    return false
  }

  const handleCancelYes = () => {
    setCancelModal(false)
    navigate(ROUTES.CLUB_HOME)
  }

  const formatTierData = formData => {
    return {
      tierId: formData.tierId || '',
      activities: formData.activities.map(activity => ({
        id: activity.activityID,
        multiplier: parseFloat(activity.multiplier) || 0,
      })),
      content: {
        ...formData.content,
      },
    }
  }

  const handleTierAction = async (isDraft = false) => {
    try {
      setIsLoading(true)
      const formData = methods.getValues()
      const tierData = formatTierData(formData)

      await createTier(tierData, isDraft)

      if (isDraft) {
        setDraftModal(false)
      }

      dispatch(
        setToastNotifications({
          show: true,
          message: formatMessage({
            id: isDraft ? 'CLUB_PLUS.TIER_CREATE_DRAFT_SUCCESS' : 'CLUB_PLUS.TIER_CREATE_SUCCESS_MESSAGE',
          }),
          type: 'success',
        })
      )
      navigate(ROUTES.CLUB_HOME)
    } catch (error) {
      console.error(`Error ${isDraft ? 'saving tier as draft' : 'publishing tier'}:`, error)
      dispatch(
        setToastNotifications({
          show: true,
          message: formatMessage({id: 'CLUB_PLUS.TIER_CREATE_ERROR_MESSAGE'}),
          type: 'error',
        })
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {
              href: '/',
              label: '',
            },
            {
              href: '/campaigns/club-plus',
              label: 'Club+',
            },
            {
              href: '/campaigns/club-plus/create-tier',
              label: 'Create Tier Mexico',
            },
          ],
        }}
        id={'ast_bradcrumb'}
      />
      <PageHeader title={'Create Tier'} />
      <FormProvider {...methods}>
        <CreateTierContainer phase={phase} setPhase={setPhase} />
        <SaveCancelFooter
          handleCancel={() => setCancelModal(true)}
          nextAction={() => {
            if (phase === 3) {
              handleTierAction(false)
            } else {
              setPhase(currentPhase => currentPhase + 1)
            }
          }}
          draftAction={() => setDraftModal(true)}
          isReview={phase === 3}
          FormError={getFormError() || isLoading}
          isLoading={isLoading}
        />
      </FormProvider>
      <ConfirmationModal
        showModal={cancelModal}
        setShowModal={setCancelModal}
        handleConfirm={handleCancelYes}
        title={formatMessage({id: 'CLUB_PLUS.TIER_CANCEL_MODAL_TITLE'})}
        text={formatMessage({id: 'CLUB_PLUS.TIER_CANCEL_MODAL_TEXT'})}
        primaryButtonText={formatMessage({id: 'GENERAL.YES'})}
        secondaryButtonText={formatMessage({id: 'GENERAL.NO'})}
        type="S"
        className="create-tier-modal"
      />
      <ConfirmationModal
        showModal={draftModal}
        setShowModal={setDraftModal}
        handleConfirm={() => handleTierAction(true)}
        title={formatMessage({id: 'CLUB_PLUS.TIER_CREATE_DRAFT_TITLE'})}
        text={formatMessage({id: 'CLUB_PLUS.TIER_CREATE_DRAFT_TEXT'})}
        primaryButtonText={formatMessage({id: 'GENERAL.YES'})}
        secondaryButtonText={formatMessage({id: 'GENERAL.NO'})}
        type="S"
        className="create-tier-modal"
      />
    </>
  )
}
